// src/components/SponsoredRow.jsx
// iter378 (v14) — Homepage row for DIRECT sponsorship campaigns only
// (placement="homepage"). Renders NOTHING when there are no active
// campaigns — no heading, no blank space. Max 3 cards.
// Impressions are viewability-gated: counted once per card per page view,
// only when >= 50% of the card is visible (IntersectionObserver).
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../utils/currency";
import { guardImage } from "../lib/imageTrust";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function track(eventType, item) {
  try {
    fetch(`${API_URL}/api/ads/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        placement: "homepage_sponsored",
        adId: String(item?.ad_id || item?.id),
        isSponsored: true,
        sponsorshipId: item?.sponsorship_id,
      }),
    });
  } catch {
    // beacons never break the UI
  }
}

function SponsoredCard({ item, isArabic, label, onOpen, onViewable }) {
  const ref = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.intersectionRatio >= 0.5 && !firedRef.current) {
          firedRef.current = true;
          onViewable?.();
          obs.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [onViewable]);

  const img = item?.image || (Array.isArray(item?.images) ? item.images[0] : null);
  const imageUrl = img ? guardImage(img.startsWith("http") ? img : `${API_URL}/api${img}`, item?.category) : null;
  const price = formatPrice(item?.price, item?.currency || "SDG", isArabic ? "ar" : "en");

  return (
    <button
      ref={ref}
      type="button"
      className="hsr-card"
      onClick={onOpen}
      data-testid="home-sponsored-card"
    >
      <div className="hsr-card-inner">
        <div className="hsr-card-image">
          {imageUrl ? (
            <img src={imageUrl} alt={item?.title || "sponsored"} loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="hsr-no-image" />
          )}
          <span className="hsr-sponsored-badge" data-testid="home-sponsored-label">{label}</span>
        </div>
        <div className="hsr-card-content">
          <div className="hsr-card-title">{item?.title || ""}</div>
          <div className="hsr-card-price">{price}</div>
          {item?.location ? <div className="hsr-card-location">{item.location}</div> : null}
        </div>
      </div>
    </button>
  );
}

export default function SponsoredRow({ isArabic }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const impressionSent = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ads/sponsored?placement=homepage&limit=3`);
        if (!res.ok) return;
        const json = await res.json();
        const list = (Array.isArray(json?.items) ? json.items : [])
          .filter((i) => i?.sponsorship_id)
          .slice(0, 3);
        if (!cancelled) setItems(list);
      } catch {
        // row stays hidden on any failure
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!items.length) return null;

  const label = t("adDetails.sponsored");

  return (
    <section
      className="home-sponsored-row"
      dir={isArabic ? "rtl" : "ltr"}
      data-testid="home-sponsored-row"
    >
      <div className="hsr-scroll">
        {items.map((item) => (
          <SponsoredCard
            key={item.sponsorship_id}
            item={item}
            isArabic={isArabic}
            label={label}
            onOpen={() => {
              track("click", item);
              navigate(`/ad/${item.ad_id || item.id}`);
            }}
            onViewable={() => {
              if (impressionSent.current.has(item.sponsorship_id)) return;
              impressionSent.current.add(item.sponsorship_id);
              track("impression", item);
            }}
          />
        ))}
      </div>
      <style>{`
        .home-sponsored-row { margin: 4px 16px 10px; }
        .hsr-scroll {
          display: flex; gap: 12px; overflow-x: auto;
          -webkit-overflow-scrolling: touch; scrollbar-width: none;
          padding-bottom: 4px;
        }
        .hsr-scroll::-webkit-scrollbar { display: none; }
        .hsr-card {
          width: 170px; min-width: 170px; flex-shrink: 0;
          background: none; border: none; padding: 0; cursor: pointer;
          text-align: start; transition: transform 0.15s ease;
        }
        .hsr-card:active { transform: scale(0.98); }
        .hsr-card-inner {
          border-radius: 16px; background: #fff; overflow: hidden;
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }
        .hsr-card-image { position: relative; height: 112px; background: rgba(0,0,0,0.05); }
        .hsr-card-image img { width: 100%; height: 100%; object-fit: cover; }
        .hsr-no-image { width: 100%; height: 100%; }
        .hsr-sponsored-badge {
          position: absolute; top: 8px; inset-inline-start: 8px;
          padding: 4px 8px; border-radius: 999px;
          font-size: 11px; font-weight: 700;
          background: #f97316; color: #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.15);
        }
        .hsr-card-content { padding: 10px 12px 12px; }
        .hsr-card-title {
          font-size: 14px; font-weight: 600; color: rgba(0,0,0,0.85);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .hsr-card-price { margin-top: 4px; font-size: 14px; font-weight: 700; color: #195B2F; }
        .hsr-card-location {
          margin-top: 4px; font-size: 12px; color: rgba(0,0,0,0.55);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
      `}</style>
    </section>
  );
}
